import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'
import { RoomShellResult, WallSide, WallSpec, buildRoomShell } from './roomShell'

export interface RoomWallConfig {
  kind: 'solid' | 'glass'
  /** Hex color for a solid wall's material — ignored for glass. */
  color?: string
}

export type RoomWalls = Record<WallSide, RoomWallConfig>

/** All four walls solid, same color — the common sealed-room case (SpacesStudio's bedroom/studio/living room). */
export function solidWalls(color: string): RoomWalls {
  return {
    left: { kind: 'solid', color },
    right: { kind: 'solid', color },
    back: { kind: 'solid', color },
    front: { kind: 'solid', color },
  }
}

/** Swaps the given sides of an existing wall set to glass (e.g. Mountain Lot: front + right). */
export function withGlassWalls(base: RoomWalls, sides: WallSide[]): RoomWalls {
  const next = { ...base }
  sides.forEach((side) => {
    next[side] = { kind: 'glass' }
  })
  return next
}

export interface RoomTemplate {
  width: number
  height: number
  depth: number
  floorColor: string
  /** Defaults match SpacesStudio's original floor finish — override for a screen with its own look (Mountain Lot's wood floor). */
  floorSpecularColor?: string
  floorSpecularPower?: number
  ceilColor: string
  walls: RoomWalls
}

export interface TemplatedShellGlass {
  material: BABYLON.Material
  mullionMaterial: BABYLON.Material
  mullionEvery?: number
}

/**
 * Turns a `RoomTemplate`'s per-wall color config into real materials and
 * calls `buildRoomShell` — the "config becomes geometry" step that lets a
 * room be authored as data (see SpacesStudio's `ROOM_PRESETS`) instead of a
 * bespoke builder function. `glass` is only required if the template has at
 * least one `kind: 'glass'` wall.
 */
export function buildTemplatedRoomShell(
  scene: BABYLON.Scene,
  bag: DisposerBag,
  template: RoomTemplate,
  glass?: TemplatedShellGlass,
): RoomShellResult {
  const floorMat = bag.track(new BABYLON.StandardMaterial('roomkit-floormat', scene))
  floorMat.diffuseColor = BABYLON.Color3.FromHexString(template.floorColor)
  floorMat.specularColor = BABYLON.Color3.FromHexString(template.floorSpecularColor ?? '#2a2420')
  floorMat.specularPower = template.floorSpecularPower ?? 90

  const ceilMat = bag.track(new BABYLON.StandardMaterial('roomkit-ceilmat', scene))
  ceilMat.diffuseColor = BABYLON.Color3.FromHexString(template.ceilColor)
  ceilMat.specularColor = BABYLON.Color3.Black()
  ceilMat.backFaceCulling = false

  const walls: Partial<Record<WallSide, WallSpec>> = {}
  ;(Object.keys(template.walls) as WallSide[]).forEach((side) => {
    const cfg = template.walls[side]
    if (cfg.kind === 'glass') {
      if (!glass) throw new Error(`RoomTemplate wall "${side}" is glass but no glass materials were provided`)
      walls[side] = { kind: 'glass', material: glass.material, mullionMaterial: glass.mullionMaterial, mullionEvery: glass.mullionEvery }
    } else {
      const mat = bag.track(new BABYLON.StandardMaterial(`roomkit-wallmat-${side}`, scene))
      mat.diffuseColor = BABYLON.Color3.FromHexString(cfg.color ?? '#403550')
      mat.specularColor = BABYLON.Color3.Black()
      mat.backFaceCulling = false
      mat.twoSidedLighting = true
      walls[side] = { kind: 'solid', material: mat }
    }
  })

  return buildRoomShell(scene, bag, {
    width: template.width,
    height: template.height,
    depth: template.depth,
    floorMaterial: floorMat,
    ceilMaterial: ceilMat,
    walls,
  })
}
